"use client";

import React, { useState, useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";

import LoginHeader from "./components/LoginHeader.js";
import SideNavigation from "./components/SideNavigation.js";
import AADHeader from "./components/AADHeader.js";

import "react-multi-carousel/lib/styles.css";
import "react-calendar/dist/Calendar.css";

import { appInsights } from "./AppInsights";
import LatLng from "./country-codes-lat-long.json";

import { MultiSelect } from "primereact/multiselect";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsExporting from "highcharts/modules/exporting";
import HighchartsOfflineExporting from "highcharts/modules/offline-exporting";
import HighchartsFullscreen from "highcharts/modules/full-screen";
import highchartsMore from "highcharts/highcharts-more";
import HighchartsMap from "highcharts/modules/map";
import mapDataWorld from "@highcharts/map-collection/custom/world.geo.json";
import proj4 from "proj4";

import CircularProgress from "@mui/material/CircularProgress";
import { Typography, Backdrop, Breadcrumbs, Link } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { Button } from "@mui/material";
import { MdRefresh } from "react-icons/md";
import "./components/Travel_Logistic/BTCEC.css";

// Whole-number look for values >=1; 2 decimals when 0<|v|<1 so small values aren't rendered as 0.
const formatMetricValue = (value) => {
  if (!Number.isFinite(value) || value === 0) return "0";
  if (Math.abs(value) >= 1) return new Intl.NumberFormat().format(Math.round(value));
  return Number(value.toFixed(2)) !== 0 ? value.toFixed(2) : String(Number(value.toPrecision(2)));
};

// Numeric variant of formatMetricValue for chart data points.
const toMetricNumber = (value) => {
  if (!Number.isFinite(value)) return 0;
  if (Math.abs(value) >= 1) return Math.round(value);
  const twoDp = Number(value.toFixed(2));
  return twoDp !== 0 ? twoDp : Number(value.toPrecision(2));
};

// No Data Component — use Router Link so navigation works even if overlays block synthetic clicks
const NoDataComponent = ({ title, configurePath = "/configuration" }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      height: "250px",
      backgroundColor: "#f8f9fa",
      borderRadius: "8px",
      border: "1px dashed #dee2e6",
      textAlign: "center",
      padding: "15px",
      position: "relative",
      zIndex: 2,
    }}
  >
    <div style={{ marginBottom: "15px" }}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M9 17H7V10H9M13 17H11V7H13M17 17H15V13H17M19.5 19.1H4.5V5H19.5M19.5 3H4.5C3.4 3 2.5 3.9 2.5 5V19.1C2.5 20.2 3.4 21.1 4.5 21.1H19.5C20.6 21.1 21.5 20.2 21.5 19.1V5C21.5 3.9 20.6 3 19.5 3Z" fill="#6c757d" />
      </svg>
    </div>
    <h6 style={{ color: "#6c757d", marginBottom: "8px", fontFamily: "Poppins", fontSize: "14px" }}>No Data Available</h6>
    <p style={{ color: "#6c757d", marginBottom: "15px", fontSize: "12px" }}>Please configure the data for visual</p>
    <Button
      component={RouterLink}
      to={configurePath}
      variant="contained"
      style={{
        backgroundColor: "#407775",
        color: "white",
        textTransform: "none",
        fontFamily: "Poppins",
        borderRadius: "6px",
        padding: "6px 16px",
        fontSize: "12px",
      }}
    >
      Configure Data
    </Button>
  </div>
);

// Initialize the exporting module
HighchartsExporting(Highcharts);
HighchartsOfflineExporting(Highcharts);
HighchartsFullscreen(Highcharts);
HighchartsMap(Highcharts);
highchartsMore(Highcharts);
Highcharts.setOptions({
  colors: ["#407775", "#fab430", "#c8e1e0", "#b8d9d8", "#b8d9d8", "#b8d9d8", "#b8d9d8", "#fcd283", "#fde4b4", "#fbc051", "#e09405", "#fef6e6"],
  chart: {
    style: {
      fontFamily: "Poppins, sans-serif",
    },
    backgroundColor: "#ffffff",
    spacingTop: 10,
    spacingBottom: 10,
    spacingLeft: 10,
    spacingRight: 10,
  },
  title: {
    style: {
      fontSize: "14px",
      fontWeight: "400",
      color: "#707070",
    },
    margin: 10,
  },
  legend: {
    itemStyle: {
      fontSize: "12px",
      fontWeight: "400",
      color: "#494949",
    },
    itemMarginTop: 2,
    itemMarginBottom: 2,
    margin: 5,
  },
  credits: {
    enabled: false,
  },
  plotOptions: {
    series: {
      marker: {
        radius: 3,
      },
    },
  },
});

// Safe read of filter array from localStorage (avoids null/undefined and JSON errors on refresh)
function getStoredFilter(key) {
  try {
    if (typeof window === "undefined") return [];
    const v = localStorage.getItem(key);
    if (v == null || v === "") return [];
    const p = JSON.parse(v);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

// Treat explicit "Select All" the same as the empty "All" placeholder.
function normalizeFilterSelection(selected, allOptions) {
  if (!Array.isArray(selected) || selected.length === 0) return [];
  if (!Array.isArray(allOptions) || allOptions.length === 0) return selected;
  if (selected.length !== allOptions.length) return selected;
  const selectedSet = new Set(selected.map((value) => String(value).toLowerCase()));
  const allSelected = allOptions.every((option) => selectedSet.has(String(option).toLowerCase()));
  return allSelected ? [] : selected;
}

export default function Home() {
  const username = localStorage.getItem("userName");
  const companyid = localStorage.getItem("companyid");
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 768);
  const [isLoading, setIsLoading] = useState(false);
  const [loading, setLoading] = React.useState(false);
  const [emissionbased, setEmissionBased] = React.useState(false);
  const [news, setNews] = useState(false);
  const [responseConfig, setResponseConfig] = useState({});
  const [legal, setLegal] = useState(() => getStoredFilter("legal1"));
  const [business, setBusiness] = useState(() => getStoredFilter("business1"));
  // Site filter: start empty so "no filter" is the default; user selection still saved to localStorage for the session
  const [site, setSite] = useState([]);
  const [year, setYear] = useState(() => getStoredFilter("year1"));
  const [country, setCountry] = useState(() => getStoredFilter("country1"));
  const [allSupplier, setAllSupplier] = useState("");
  const [supplier, setSupplier] = useState([]);
  const [employee, setEmployee] = useState("");
  const [scope, setScope] = useState("");
  const [yearr, setYearR] = useState("");
  const [countries, setCountries] = useState();
  const [businessunit, setBusinessUnit] = useState();
  const [activesite, setActiveSite] = useState();
  const [activeetities, setActiveEntities] = useState();
  const [checked, setChecked] = useState(true);
  const [viewMode, setViewMode] = useState("marketBased");
  const [scope1Emission, setScope1Emission] = useState("");
  const [energyConsumption, setEnergyConsumption] = useState("");
  const [emissionIntensity, setEmissionIntensity] = useState("");
  const [activeyears, setActiveYears] = useState();

  if (typeof window !== "undefined") {
    window.proj4 = window.proj4 || proj4;
  }

  function truncateText(text, maxLength) {
    if (text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  }


  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  async function fetchFilters(overrides = {}) {
    try {
      const country = normalizeFilterSelection(overrides.country ?? getStoredFilter("country1"), countries);
      const year = normalizeFilterSelection(overrides.year ?? getStoredFilter("year1"), activeyears);
      const legal = normalizeFilterSelection(overrides.legal ?? getStoredFilter("legal1"), activeetities);
      const business = normalizeFilterSelection(overrides.business ?? getStoredFilter("business1"), businessunit);
      const siteVal = normalizeFilterSelection(overrides.site ?? site, activesite);
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}scope1-filters`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          username: username,
        },
        body: JSON.stringify({
          companyId: companyid,
          year: year,
          legalEntity: legal,
          businessUnit: business,
          country: country,
          site: siteVal,
        }),
      });
      if (response.status === 200) {
        const responseJson = await response.json();
        if (responseJson.success === false) {
          setActiveEntities("");
          setActiveYears("");
          setBusinessUnit("");
          setCountries("");
          setActiveSite("");
        } else if (responseJson.status == 400) {
          setActiveEntities("");
          setActiveYears("");
          setBusinessUnit("");
          setCountries("");
          setActiveSite("");
        } else {
          setActiveEntities(responseJson.legalEntity);
          setActiveYears(responseJson.year);
          setBusinessUnit(responseJson.businessUnit);
          setCountries(responseJson.country);
          setActiveSite(responseJson.site);
        }
      } else {
        setActiveEntities("");
        setActiveYears("");
        setBusinessUnit("");
        setCountries("");
        setActiveSite("");
      }
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  var myHeaders = new Headers();
  myHeaders.append("username", username);
  var requestOptions = {
    method: "GET",
    headers: myHeaders,
    redirect: "follow",
  };

  async function fetchScope1(overrides = {}) {
    try {
      setLoading(true);
      const companyid = localStorage.getItem("companyid");
      var myHeaders = new Headers();
      myHeaders.append("username", username);
      var requestOptions = {
        method: "GET",
        headers: myHeaders,
        redirect: "follow",
      };
      const country = normalizeFilterSelection(overrides.country ?? getStoredFilter("country1"), countries);
      const year = normalizeFilterSelection(overrides.year ?? getStoredFilter("year1"), activeyears);
      const legal = normalizeFilterSelection(overrides.legal ?? getStoredFilter("legal1"), activeetities);
      const business = normalizeFilterSelection(overrides.business ?? getStoredFilter("business1"), businessunit);
      const siteVal = normalizeFilterSelection(overrides.site ?? site, activesite);
      const response = await fetch(`${process.env.REACT_APP_BASE_API_URL}scope1`, {
        method: "POST",
        headers: {
          "Content-type": "application/json; charset=UTF-8",
          username: username,
        },
        body: JSON.stringify({
          companyId: companyid,
          year: year,
          legalEntity: legal,
          businessUnit: business,
          country: country,
          site: siteVal,
        }),
      });
      if (response.status === 200) {
        const responseJson = await response.json();
        if (responseJson.success === false || responseJson.status === 400) {
          setLoading(false);
        } else {
          setScope(responseJson);
          setScope1Emission(responseJson.totalEmissions);
          setEnergyConsumption(responseJson.totalElectricityConsumptionInMwh);
          setEmissionIntensity(responseJson.emissionIntensity);
          console.log(responseJson);
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  }

  useEffect(() => {
    appInsights.trackPageView();
    // Ensure filter keys exist so future reads are consistent
    if (localStorage.getItem("country1") == null) localStorage.setItem("country1", JSON.stringify([]));
    if (localStorage.getItem("year1") == null) localStorage.setItem("year1", JSON.stringify([]));
    if (localStorage.getItem("legal1") == null) localStorage.setItem("legal1", JSON.stringify([]));
    if (localStorage.getItem("business1") == null) localStorage.setItem("business1", JSON.stringify([]));
    if (localStorage.getItem("site1") == null) localStorage.setItem("site1", JSON.stringify([]));
    fetchScope1();
    fetchFilters();
  }, []);

  const handleLegalChange = (selectedLegal) => {
    setLegal(selectedLegal);
    localStorage.setItem("legal1", JSON.stringify(selectedLegal));
    fetchScope1();
    fetchFilters();
  };

  const handleBusinessChange = (selectedBusiness) => {
    setBusiness(selectedBusiness);
    localStorage.setItem("business1", JSON.stringify(selectedBusiness));
    fetchScope1();
    fetchFilters();
  };

  const handleCountryChange = (selectedCountry) => {
    setCountry(selectedCountry);
    localStorage.setItem("country1", JSON.stringify(selectedCountry));
    fetchScope1();
    fetchFilters();
  };

  const handleSiteChange = (selectedSite) => {
    setSite(selectedSite);
    localStorage.setItem("site1", JSON.stringify(selectedSite));
    fetchScope1({ site: selectedSite });
    fetchFilters({ site: selectedSite });
  };

  const handleYearChange = (selectedYear) => {
    setYear(selectedYear);
    localStorage.setItem("year1", JSON.stringify(selectedYear));
    fetchScope1();
    fetchFilters();
  };

  const handleResetFilters = async () => {
    try {
      setLoading(true);
      setLegal([]);
      setBusiness([]);
      setCountry([]);
      setSite([]);
      setYear([]);

      localStorage.removeItem("legal1");
      localStorage.removeItem("business1");
      localStorage.removeItem("country1");
      localStorage.removeItem("site1");
      localStorage.removeItem("year1");

      // Pass explicit empty filters so API gets [] immediately (setState is async)
      const emptyFilters = { legal: [], business: [], country: [], site: [], year: [] };
      await fetchFilters(emptyFilters);
      await fetchScope1(emptyFilters);
    } catch (error) {
      console.error("Error resetting filters:", error);
    } finally {
      setLoading(false);
    }
  };
  const pieChartOptions = {
    chart: {
      type: "pie",
      height: 400,
      spacingBottom: 30,
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      pointFormat: "{series.name}: <b>{point.y} tCO2e</b> ({point.percentage:.1f}%)",
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "{point.percentage:.1f}%",
          distance: 20,
          style: {
            fontSize: "12px",
            fontWeight: "400",
            color: "#333",
          },
          connectorShape: "crookedLine",
          crookDistance: "70%",
          connectorPadding: 5,
          connectorWidth: 1.5,
          connectorColor: "#333",
        },
        showInLegend: true,
        borderColor: null,
        size: "55%",
      },
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        fontSize: "12px",
        fontWeight: "400",
        color: "#494949",
      },
      symbolRadius: 50,
      symbolHeight: 14,
      symbolWidth: 14,
      y: 15,
    },
    series: [
      {
        name: "Emission (tCO2e)",
        colorByPoint: true,
        data: [
          {
            name: "Stationary",
            y: 3365,
            color: "#407775",
          },
          {
            name: "Mobile",
            y: 5503,
            color: "#FAB430",
          },
        ],
      },
    ],
  };

  // Emission by categories
  const energyMix = Object.keys((scope && scope.emissionMixBySite) || {});
  const energyMixCategories = energyMix.map((site) => site);
  const energyMixemission = energyMix.map((site) => scope && scope.emissionMixBySite[site].totalEmissions);

  const siteResponseRateOptions = {
    chart: {
      type: "bar",
      height: 350,
      spacingBottom: 30,
    },
    credits: {
      enabled: false,
    },
    title: {
      text: null,
    },
    xAxis: {
      categories: energyMixCategories,
    },
    yAxis: {
      min: 0,
      title: {
        text: "",
      },
    },
    plotOptions: {
      bar: {
        dataLabels: {
          enabled: true,
          format: "{y}",
          style: {
            fontWeight: "400",
            color: "#333",
          },
        },
      },
    },
    legend: {
      enabled: false,
    },
    series: [
      {
        name: "Emission (tCO2e)",
        data: energyMixemission,
        color: "#9AC7BF",
      },
    ],
  };

  //RE share by month start
  const marketCategories = [];
  const marketColumnSeries = [];
  const marketLineSeries = [];

  scope &&
    scope.energyConsumptionAndREShareByMonth.forEach((data) => {
      marketCategories.push(data.month.substring(0, 3) + " " + data.year.toString().substring(2));
      marketColumnSeries.push(data.energyInMWH);
      marketLineSeries.push(data.totalEmissions);
    });

  const Data_RESM = {
    categories: marketCategories,
    columnSeries: marketColumnSeries,
    lineSeries: marketLineSeries,
  };

  const options_dualAxis = {
    chart: {
      type: "column",
      height: 350,
      spacingBottom: 30,
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: Data_RESM.categories,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
    },
    yAxis: [
      {
        title: {
          text: "Energy consumption (MWh)",
          rotation: -90,
          style: {
            fontSize: "11px",
            fontWeight: "400",
            color: "#494949",
          },
        },
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
      {
        title: {
          text: "Total emissions (tCO2e)",
          rotation: -90,
          style: {
            fontSize: "11px",
            fontWeight: "400",
            color: "#494949",
          },
        },
        opposite: true,
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
    ],
    plotOptions: {
      column: {
        stacking: "normal",
        color: "#fab430",
        dataLabels: {
          enabled: false,
          format: "{y:.0f}",
        },
      },
      line: {
        color: "#407775",
        dataLabels: {
          enabled: false,
          format: "{y:.0f}",
        },
      },
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontWeight: "400",
        color: "#494949",
      },
      shared: true,
      formatter: function () {
        let tooltip = "";
        this.points.forEach((point) => {
          let value = point.y;
          value = formatMetricValue(value);
          tooltip += `<span style="color:${point.series.color}">${point.series.name}</span>: ${value}<br/>`;
        });
        return tooltip;
      },
    },
    series: [
      {
        name: "Energy consumption (MWh)",
        data: Data_RESM.columnSeries,
        yAxis: 0,
      },
      {
        name: "Total emissions (tCO2e)",
        data: Data_RESM.lineSeries,
        type: "line",
        yAxis: 1,
      },
    ],
  };

  // emission by business unit start
  const energyConsumptionByBusinessUnit = scope && scope.emissionsAndConsumptionByBusinessUnit;
  const ecbbucategories = [];
  const ecbbuelectricityconsuption = [];
  const ecbbumarketbased = [];

  if (energyConsumptionByBusinessUnit) {
    Object.entries(energyConsumptionByBusinessUnit).forEach(([name, value]) => {
      ecbbucategories.push(name);
      ecbbuelectricityconsuption.push(value.energyInMWH);
      ecbbumarketbased.push(value.totalEmissions);
    });
  }

  const Data_emission_BU = {
    categories: ecbbucategories,
    columnSeries: ecbbuelectricityconsuption,
    lineSeries: ecbbumarketbased,
  };

  const dualAxis_emissionBU = {
    chart: {
      type: "column",
      height: 350,
      spacingBottom: 30,
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: Data_emission_BU.categories,
      gridLineWidth: 0,
      minorGridLineWidth: 0,
    },
    yAxis: [
      {
        title: {
          text: "Energy consumption (MWh)",
          rotation: -90,
          style: {
            fontSize: "11px",
            fontWeight: "400",
            color: "#494949",
          },
        },
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
      {
        title: {
          text: "Total emissions (tCO2e)",
          rotation: -90,
          style: {
            fontSize: "11px",
            fontWeight: "400",
            color: "#494949",
          },
        },
        opposite: true,
        gridLineWidth: 0,
        minorGridLineWidth: 0,
      },
    ],
    plotOptions: {
      column: {
        stacking: "normal",
        color: "#fab430",
        dataLabels: {
          enabled: false,
          format: "{y:.0f}",
        },
      },
      line: {
        color: "#407775",
        dataLabels: {
          enabled: false,
          format: "{y:.0f}",
        },
      },
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontWeight: "400",
        color: "#494949",
      },
      shared: true,
      formatter: function () {
        let tooltip = "";
        this.points.forEach((point) => {
          let value = point.y;
          value = formatMetricValue(value);
          tooltip += `<span style="color:${point.series.color}">${point.series.name}</span>: ${value}<br/>`;
        });
        return tooltip;
      },
    },
    series: [
      {
        name: "Energy consumption (MWh)",
        data: Data_emission_BU.columnSeries,
        yAxis: 0,
      },
      {
        name: "Total emissions (tCO2e)",
        data: Data_emission_BU.lineSeries,
        type: "line",
        yAxis: 1,
      },
    ],
  };

  // Emission by site type start
  const emissionSiteData = scope && scope.energyBySiteType;
  const data_emissionsite = {
    categories: Object.keys(emissionSiteData || {}),
    values: Object.values(emissionSiteData || {}).map((emission) => emission.energyInMWH),
  };

  const optionsPieChart = {
    chart: {
      type: "pie",
      height: 350,
      spacingBottom: 30,
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    legend: {
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: "#494949",
        fontWeight: "400",
        fontSize: "12px",
      },
      y: 15,
    },
    plotOptions: {
      pie: {
        innerSize: "50%",
        allowPointSelect: true,
        cursor: "pointer",
        dataLabels: {
          enabled: true,
          format: "{point.percentage:.0f}%",
          style: {
            fontWeight: "400",
          },
        },
        showInLegend: true,
        size: "75%",
      },
    },
    series: [
      {
        name: "Energy consumption (MWh)",
        data: data_emissionsite.categories.map((site, index) => ({
          name: site,
          y: toMetricNumber(data_emissionsite.values[index]),
        })),
        tooltip: {
          style: {
            fontSize: "12px",
            fontWeight: "400",
            color: "#494949",
          },
          pointFormatter: function () {
            return '<span style="color:' + this.series.color + '">' + this.series.name + "</span>: " + formatMetricValue(this.y) + " (" + this.percentage.toFixed(0) + "%)<br/>";
          },
        },
      },
    ],
  };

  // Map data
  const mapData =
    scope &&
    scope.emissionsAndConsumptionByCountry
      .map((country) => {
        const usLatLng = LatLng.find((item) => item.country === country.country);
        if (usLatLng) {
          const formattedZ = Number.parseFloat(country.totalEmissions.toFixed(2));
          const formattedY = Number.parseFloat(country.energyInMWH.toFixed(2));
          return {
            z: formattedZ,
            Y: formattedY,
            keyword: country.country,
            name: country.country,
            lat: usLatLng.latitude,
            lon: usLatLng.longitude,
          };
        }
        return null;
      })
      .filter((data) => data !== null);

  const mapOptions = {
    chart: {
      map: "custom/world",
      height: 400,
      spacingBottom: 30,
    },
    title: {
      text: null,
    },
    credits: {
      enabled: false,
    },
    mapNavigation: {
      enabled: true,
    },
    tooltip: {
      style: {
        fontSize: "12px",
        fontWeight: "400",
        color: "#494949",
      },
      headerFormat: "",
      formatter: function () {
        return "<b>" + this.point.keyword + "</b><br>Emission (tCO2e): " + formatMetricValue(this.point.z) + "<br>Energy Consumption (MWh): " + formatMetricValue(this.point.Y);
      },
    },
    series: [
      {
        name: "",
        mapData: mapDataWorld,
        borderColor: "#A0A0A0",
        nullColor: "rgba(200, 200, 200, 0.3)",
        showInLegend: false,
      },
      {
        type: "mapbubble",
        name: "",
        color: "#fab430",
        data: mapData,
        cursor: "pointer",
        showInLegend: false,
        dataLabels: {
          enabled: true,
          format: "{point.name}",
        },
        point: {
          events: {
            click: function () {
              console.log(this.keyword);
            },
          },
        },
      },
    ],
  };

  const yearss = activeyears && activeyears.map((status) => status);
  const activelegal = activeetities && activeetities.map((status) => status);
  const activebusiness = businessunit && businessunit.map((status) => status);
  const activesites = activesite && activesite.map((status) => status);
  const activecountry = countries && countries.map((status) => status);

  const renderHeader = () => {
    const loginType = localStorage.getItem("loginType");
    if (loginType === "AAD") {
      return <AADHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
    }
    return <LoginHeader toggleSidebar={toggleSidebar} isSidebarOpen={isSidebarOpen} heading="" />;
  };

  return (
    <div id="wrapper">
      <div>
        {loading && (
          <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}
      </div>
      {renderHeader()}
      <SideNavigation isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div id="page-content-wrapper" className={isSidebarOpen ? "page-content-wrapper-pd-0" : "page-wrapper"}>
        <div className="container formpd">
          <div className="row">
            <div className="col-12 col-md-6">
              <div className="steps__containt-hader">
                <h3 className="title">Scope 1 Emissions</h3>
                <div role="presentation">
                  <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/esg-dashboard" className="link">
                      Dashboard
                    </Link>
                    <Typography color="text.primary">Scope 1 Emissions</Typography>
                  </Breadcrumbs>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-6 d-flex align-items-center justify-content-md-end flex-wrap gap-2">
              <div className="row justify-content-md-end my-2">
                <div className="col-auto">
                  <Button className="refresh-cta" onClick={handleResetFilters} variant="text" startIcon={<MdRefresh />}>
                    Reset Filters
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-3">
            {[
              {
                label: "Legal entity",
                value: legal,
                setValue: setLegal,
                handleChange: handleLegalChange,
                options: activelegal,
              },
              {
                label: "Business unit",
                value: business,
                setValue: setBusiness,
                handleChange: handleBusinessChange,
                options: activebusiness,
              },
              {
                label: "Country",
                value: country,
                setValue: setCountry,
                handleChange: handleCountryChange,
                options: activecountry,
              },
              {
                label: "Site location",
                value: site,
                setValue: setSite,
                handleChange: handleSiteChange,
                options: activesites,
              },
              { label: "Year", value: year, setValue: setYear, handleChange: handleYearChange, options: yearss },
            ].map(({ label, value, setValue, handleChange, options }, index) => (
              <div key={index} className="col-12 col-sm-6 col-md-4 col-lg-2">
                <label className="form-label">{label}</label>
                <MultiSelect
                  value={value}
                  onChange={(e) => {
                    setValue(e.target.value);
                    handleChange(e.target.value);
                  }}
                  options={options}
                  placeholder="All"
                  maxSelectedLabels={1}
                  className="w-100"
                  showClear
                  showSelectAll
                  selectAllLabel="Select All"
                />
              </div>
            ))}
          </div>

          {scope && (
            <div>
              {/* Emission Summary */}
              <div className="row mt-4 g-3">
                {[
                  { title: "Scope 1 emission", value: scope1Emission, unit: "tCO2e" },
                  { title: "Total energy consumption", value: energyConsumption, unit: "MWh" },
                  { title: "Scope 1 emission intensity", value: emissionIntensity, unit: "tCO2e/mn EUR" },
                ].map((item, index) => (
                  <div key={index} className="col-12 col-sm-6 col-md-4 d-flex">
                    <div className="employee-box" style={{ width: "100%", display: "flex", flexDirection: "column" }}>
                      <div className="employee-box-header-scope2">
                        {item.title} <span className="dashboard-value">({item.unit})</span>
                      </div>
                      <div
                        className="employee-box-content"
                        style={{
                          flex: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                        }}
                      >
                        {item.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Charts Section */}
              <div className="row mt-3 g-3">
                <div className="col-12 col-lg-8 d-flex">
                  <div className="charts BTCEC-main" style={{ width: "100%", display: "flex", flexDirection: "column" }}>
                    {scope && scope.totalEmissions > 0 ? (
                      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <div
                          className="chart-title"
                          style={{
                            textAlign: "left",
                            marginBottom: "10px",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: "#707070",
                            marginLeft: "10px",
                          }}
                        >
                          Total energy consumption (MWh) and emissions(tCO2e) footprint
                        </div>
                        <div style={{ flex: 1 }}>
                          <HighchartsReact constructorType={"mapChart"} highcharts={Highcharts} options={mapOptions} />
                        </div>
                      </div>
                    ) : (
                      <NoDataComponent title="Global Emissions Map" />
                    )}
                  </div>
                </div>
                <div className="col-12 col-lg-4 d-flex">
                  <div className="charts BTCEC-main" style={{ width: "100%", display: "flex", flexDirection: "column" }}>
                    {scope && scope.totalEmissions > 0 ? (
                      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <div
                          className="chart-title"
                          style={{
                            textAlign: "left",
                            marginBottom: "10px",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: "#707070",
                            marginLeft: "10px",
                          }}
                        >
                          Emissions by categories
                        </div>
                        <div style={{ flex: 1 }}>
                          <HighchartsReact highcharts={Highcharts} options={pieChartOptions} />
                        </div>
                      </div>
                    ) : (
                      <NoDataComponent title="Emissions by Source" />
                    )}
                  </div>
                </div>

                {/* Energy Consumption */}
                <div className="col-12 col-lg-8 d-flex">
                  <div className="charts BTCEC-main" style={{ width: "100%", display: "flex", flexDirection: "column" }}>
                    {scope && scope.totalEmissions > 0 ? (
                      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <div
                          className="chart-title"
                          style={{
                            textAlign: "left",
                            marginBottom: "10px",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: "#707070",
                            marginLeft: "10px",
                          }}
                        >
                          Energy consumption (MWh) and Emissions (tCO2e) by Month
                        </div>
                        <div style={{ flex: 1 }}>
                          <HighchartsReact highcharts={Highcharts} options={options_dualAxis} />
                        </div>
                      </div>
                    ) : (
                      <NoDataComponent title="Energy Consumption Trends" />
                    )}
                  </div>
                </div>
                <div className="col-12 col-lg-4 d-flex">
                  <div className="charts BTCEC-main" style={{ width: "100%", display: "flex", flexDirection: "column" }}>
                    {scope && scope.totalEmissions > 0 ? (
                      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <div
                          className="chart-title"
                          style={{
                            textAlign: "left",
                            marginBottom: "10px",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: "#707070",
                            marginLeft: "10px",
                          }}
                        >
                          Emissions (tCO2e) by site
                        </div>
                        <div style={{ flex: 1 }}>
                          <HighchartsReact highcharts={Highcharts} options={siteResponseRateOptions} />
                        </div>
                      </div>
                    ) : (
                      <NoDataComponent title="Site Response Rate" />
                    )}
                  </div>
                </div>

                {/* Energy Emission */}
                <div className="col-12 col-lg-6 d-flex">
                  <div className="charts BTCEC-main" style={{ width: "100%", display: "flex", flexDirection: "column" }}>
                    {scope && scope.totalEmissions > 0 ? (
                      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <div
                          className="chart-title"
                          style={{
                            textAlign: "left",
                            marginBottom: "10px",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: "#707070",
                            marginLeft: "10px",
                          }}
                        >
                          Energy consumption (MWh) by Site Type
                        </div>
                        <div style={{ flex: 1 }}>
                          <HighchartsReact highcharts={Highcharts} options={optionsPieChart} />
                        </div>
                      </div>
                    ) : (
                      <NoDataComponent title="Emissions by Source Type" />
                    )}
                  </div>
                </div>
                <div className="col-12 col-lg-6 d-flex">
                  <div className="charts BTCEC-main" style={{ width: "100%", display: "flex", flexDirection: "column" }}>
                    {scope && scope.totalEmissions > 0 ? (
                      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <div
                          className="chart-title"
                          style={{
                            textAlign: "left",
                            marginBottom: "10px",
                            fontSize: "14px",
                            fontWeight: "400",
                            color: "#707070",
                            marginLeft: "10px",
                          }}
                        >
                          Energy consumption (MWh) and Emissions by BU (tCO2e)
                        </div>
                        <div style={{ flex: 1 }}>
                          <HighchartsReact highcharts={Highcharts} options={dualAxis_emissionBU} />
                        </div>
                      </div>
                    ) : (
                      <NoDataComponent title="Emissions by Business Unit" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
